import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type {
  AssistantConversationDTO, AssistantObjectRef, AssistantPendingDTO, AssistantSseEvent, AssistantTurnContext,
  AssistantUndoableDTO,
} from '@contracts/assistant';
import type { Id } from '@contracts/common';
import { ApiClientError, offlineError } from '../../api/client';
import { useAssistant } from './AssistantProvider';
import {
  CONVERSATIONS_KEY, POLL_FOR_MS, actionPath, conversationKey, createConversation, turnPath,
} from './api';
import { usePageContext } from './page-context';
import { readTurn } from './stream';
import { createReply, createTypewriter, type Typewriter } from './typewriter';

/** `unknown`: an `ext.call` that may or may not have gone out (EXT_UNKNOWN, §13 "không chắc"), never a failure. */
export type LiveStep = { callId: string; label: string; state: 'running' | 'ok' | 'failed' | 'unknown' };
/** A finished step's state from its `ok` and error code (the live `tool_done` and a stored step alike). */
export const doneState = (s: { ok: boolean; errorCode?: string }): LiveStep['state'] =>
  (s.ok ? 'ok' : s.errorCode === 'EXT_UNKNOWN' ? 'unknown' : 'failed');
export type LiveTurn = {
  userText: string; reply: string; typing: boolean; steps: LiveStep[];
  error: { code: string; message: string } | null; done: boolean;
  /** S8 §7.1: the reply is the scope gate's refusal (D53: the list marks it before the stored message lands). */
  blocked: boolean;
  /** The stored messages when the turn began: the list shows the live turn until a new assistant message is stored. */
  known: readonly Id[];
};
export type TurnHandlers = {
  /** A step ended: the objects it touched, and whether it succeeded. */
  onObjects(objects: AssistantObjectRef[], ok: boolean): void;
  onUndo(u: AssistantUndoableDTO): void;
  /**
   * The server refused the turn, the confirm or the reject. Called even after the caller unmounted: the Map's draft bar
   * goes with the card it confirms, and still says why a confirm was refused (D206).
   */
  onRefused(refusal: Refusal): void;
};
type Refusal = { code: string; message: string };
/**
 * One request's own view of its answer (S6 §7.3, D47: an external card follows its Xác nhận): every streamed event, a
 * JSON answer's `data`, and a refusal — `onRefused` answering true says the caller shows it, so the composer does not.
 */
export type ActTap = {
  onEvent?(e: AssistantSseEvent): void;
  onJson?(data: unknown): void;
  onRefused?(refusal: Refusal): boolean;
};

/** D121: shown when the conversation still runs after five minutes of polling. */
export const NO_ANSWER_MESSAGE = 'Chưa nhận được câu trả lời. Mở lại hội thoại sau ít phút.';

export function prefersReducedMotion(): boolean {
  try {
    return typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}

/**
 * A card the stream announced goes into the cached conversation at once (one source for the list: the stream's card
 * and the stored one are the same card, keyed by its action). The refetch after the turn replaces it with the truth.
 */
export function withCard(c: AssistantConversationDTO | undefined, card: { pending?: AssistantPendingDTO;
  undoable?: AssistantUndoableDTO }): AssistantConversationDTO | undefined {
  if (c === undefined) return c;
  const { pending, undoable } = card;
  return {
    ...c,
    pending: pending === undefined || c.pending.some((p) => p.actionId === pending.actionId) ? c.pending
      : [...c.pending, pending],
    // D187, D188: one card per command. A later write of the same turn replaces its group's card in place.
    undoable: undoable === undefined ? c.undoable
      : c.undoable.some((u) => u.groupId === undoable.groupId)
        ? c.undoable.map((u) => (u.groupId === undoable.groupId ? undoable : u))
        : [...c.undoable, undoable],
  };
}

/** The conversation the turn was posted to is not there any more (retention, another tab deleted it: P2 review F5). */
const isGone = (e: unknown): boolean => e instanceof ApiClientError && e.status === 404 && e.code === 'NOT_FOUND';

const toRefusal = (e: unknown): Refusal => {
  const err = e instanceof ApiClientError ? e : offlineError();
  return { code: err.code, message: err.message };
};

/**
 * One turn's stream into live state (spec §6.4, D84). `send` starts a turn (creating the conversation on the first
 * message); `stream` reads any POST that may answer SSE, which Task 16's cards use for confirm and reject. Closing the
 * panel stops reading only: the server turn finishes on its own (D70, D126).
 */
export function useTurn(h: Partial<TurnHandlers> = {}): {
  live: LiveTurn | null; running: boolean;
  /**
   * `objectRef`: the object this message is about (D208), in place of the page's own. `context` (S3 D61): the nudge
   * this message asks about, which always starts a new conversation, being its first turn's only.
   */
  send: (text: string, objectRef?: AssistantObjectRef, context?: AssistantTurnContext) => Promise<'sent' | 'refused'>;
  stream: (path: string, body: unknown, userText: string | null) => Promise<'sent' | 'refused'>;
  /**
   * Confirms or rejects a card (D116): the answer may stream the resumed turn, or be JSON when there is none. `callId`
   * is the call `confirm_required` carried; left out, the one this hook saw for the action (null for a reloaded card).
   */
  act: (actionId: Id, kind: 'confirm' | 'reject', callId?: string | null, tap?: ActTap) => Promise<'sent' | 'refused'>;
  /** The call `confirm_required` carried for the action, null for a card this hook did not see stream in. */
  callOf: (actionId: Id) => string | null;
  refusal: Refusal | null;
  /** Leaves the live turn behind (a new or another conversation). */
  clear: () => void;
  /** D121: poll the conversation until this instant while it runs; null = no polling. */
  pollUntil: number | null;
} {
  const { still, feel, setConversationId, conversationId: currentId } = useAssistant();
  const queryClient = useQueryClient();
  const page = usePageContext();
  const [live, setLive] = useState<LiveTurn | null>(null);
  const [refusal, setRefusal] = useState<Refusal | null>(null);
  const [running, setRunning] = useState(false);
  const [poll, setPoll] = useState<{ id: Id; until: number } | null>(null);
  const busy = useRef(false);
  const calls = useRef(new Map<Id, string>());
  const abort = useRef<AbortController | null>(null);
  const writer = useRef<Typewriter | null>(null);
  const handlers = useRef(h);
  useEffect(() => { handlers.current = h; });
  const current = useRef(currentId);
  useEffect(() => { current.current = currentId; }, [currentId]);
  // Unmount (the panel closed) stops reading only (D84).
  useEffect(() => () => { abort.current?.abort(); writer.current?.stop(); }, []);

  // D121: after five minutes the polling gives up and says so.
  useEffect(() => {
    if (poll === null) return undefined;
    const timer = window.setTimeout(() => {
      const c = queryClient.getQueryData<AssistantConversationDTO>(conversationKey(poll.id));
      if (c?.running === true) {
        setLive((l) => (l === null ? l : { ...l, error: { code: 'NO_ANSWER', message: NO_ANSWER_MESSAGE } }));
      }
      setPoll(null);
    }, Math.max(0, poll.until - Date.now()));
    return () => window.clearTimeout(timer);
  }, [poll, queryClient]);

  /** `goneOk`: a 404 for the conversation answers 'gone' with nothing shown, so the send can start a new one. */
  const run = useCallback(async (path: string, body: unknown, userText: string | null, goneOk: boolean, tap?: ActTap):
    Promise<'sent' | 'refused' | 'gone'> => {
    if (busy.current) return 'refused' as const;
    busy.current = true;
    setRunning(true);
    setRefusal(null);
    setPoll(null);
    abort.current?.abort();
    writer.current?.stop();
    const controller = new AbortController();
    abort.current = controller;
    const steps = new Map<string, LiveStep>();
    const patch = (p: Partial<LiveTurn>) => setLive((l) => (l === null ? l : { ...l, ...p }));
    const tw = createTypewriter({ reduced: still || prefersReducedMotion(),
      onChange: (reply, typing) => { if (!controller.signal.aborted) patch({ reply, typing }); } });
    writer.current = tw;
    const reply = createReply(tw);
    let conversationId = current.current;
    const known = conversationId === null ? []
      : (queryClient.getQueryData<AssistantConversationDTO>(conversationKey(conversationId))?.messages ?? [])
        .map((m) => m.id);
    // Typing until the typewriter says it has shown everything: its first word may wait for a reserve (START_WAIT_MS),
    // and a refetch landing in that wait must not replace a reply that has not appeared yet.
    setLive({ userText: userText ?? '', reply: '', typing: true, steps: [], error: null, done: false, known,
      blocked: false });
    try {
      const answer = await readTurn(path, body, { signal: controller.signal, onEvent: (e) => {
        feel({ kind: 'event', event: e });
        switch (e.type) {
          case 'turn_started':
            conversationId = e.data.conversationId;
            setConversationId(conversationId);
            break;
          case 'thinking': reply.segment(); break;
          case 'tool_started':
            // Keyed by the call: a resume that starts the same call again shows it once.
            steps.set(e.data.callId, { callId: e.data.callId, label: e.data.label, state: 'running' });
            patch({ steps: [...steps.values()] });
            break;
          case 'tool_done': {
            const s = steps.get(e.data.callId);
            if (s !== undefined) steps.set(e.data.callId, { ...s, state: doneState(e.data) });
            patch({ steps: [...steps.values()] });
            handlers.current.onObjects?.(e.data.objects, e.data.ok);
            break;
          }
          case 'confirm_required': {
            const { callId, ...pending } = e.data;
            calls.current.set(pending.actionId, callId);
            feel({ kind: 'pending', open: true });
            if (conversationId !== null) {
              queryClient.setQueryData<AssistantConversationDTO>(conversationKey(conversationId),
                (c) => withCard(c, { pending }));
            }
            break;
          }
          case 'message_delta': reply.delta(e.data.text); break;
          case 'message_done':
            if (e.data.blocked === true) patch({ blocked: true });
            tw.settle(e.data.text);
            break;
          case 'undo_available':
            if (conversationId !== null) {
              queryClient.setQueryData<AssistantConversationDTO>(conversationKey(conversationId),
                (c) => withCard(c, { undoable: e.data }));
            }
            handlers.current.onUndo?.(e.data);
            break;
          case 'error': tw.end(); patch({ error: e.data }); break;
          case 'turn_done': tw.end(); patch({ done: true }); break;
        }
        tap?.onEvent?.(e);
      } });
      if (answer.kind === 'json') {                            // a confirm with no turn to run (D116)
        tap?.onJson?.(answer.data);
        setLive(null);
        return 'sent' as const;
      }
      tw.end();
      if (!answer.complete && !controller.signal.aborted && conversationId !== null) {
        setPoll({ id: conversationId, until: Date.now() + POLL_FOR_MS });
      }
      return 'sent' as const;
    } catch (error) {
      tw.stop();
      if (goneOk && isGone(error)) {
        setLive(null);
        conversationId = null;                                // nothing of it to read again
        return 'gone' as const;
      }
      const refused = toRefusal(error);
      if (tap?.onRefused?.(refused) !== true) setRefusal(refused);
      handlers.current.onRefused?.(refused);
      setLive(null);
      feel({ kind: 'refused' });
      return 'refused' as const;
    } finally {
      try {
        if (conversationId !== null) {
          // P4 G9: a new conversation's first read may still be out, started before the server stored this turn's card
          // (and the stream's card was dropped: nothing was cached to put it in). An invalidate would only wait for
          // that stale read, so it is cancelled first and the conversation read anew.
          await queryClient.cancelQueries({ queryKey: conversationKey(conversationId) });
          await queryClient.invalidateQueries({ queryKey: conversationKey(conversationId) });
          void queryClient.invalidateQueries({ queryKey: CONVERSATIONS_KEY });
        }
      } finally {
        // Layer re-review F6: the turn ends once its stored state is read. A stream's card is cached before its turn's
        // messages are: pressed earlier, a card would be confirmed under the previous turn's last message.
        busy.current = false;
        setRunning(false);
      }
    }
  }, [still, feel, setConversationId, queryClient]);
  const stream = useCallback((path: string, body: unknown, userText: string | null) =>
    run(path, body, userText, false) as Promise<'sent' | 'refused'>, [run]);

  const send = useCallback(async (text: string, objectRef?: AssistantObjectRef, context?: AssistantTurnContext) => {
    if (busy.current) return 'refused' as const;
    // P4 G8 review F4: a card picked on a project's embedded board lives on the Workplace, so the ask names that page
    // (the project's route would put the task's id in the project's slot).
    const onBoard = objectRef !== undefined && (objectRef.type === 'task' || objectRef.type === 'note')
      && page !== null && page.route !== '/workplace';
    // S3 D61/D128: a context ask (a nudge's, a briefing's) is about that context, not the page it was pressed on.
    const about = context !== undefined ? null : objectRef === undefined || page === null ? page
      : onBoard ? { route: '/workplace', objectRef } : { ...page, objectRef };
    const body = { text, ...(about === null ? {} : { page: about }), ...(context === undefined ? {} : { context }) };
    const start = async () => {
      try {
        const id = (await createConversation(crypto.randomUUID())).id;
        current.current = id;
        setConversationId(id);
        return id;
      } catch (e) {
        setRefusal(toRefusal(e));
        feel({ kind: 'refused' });
        return null;
      }
    };
    const kept = context === undefined ? current.current : null;
    if (kept !== null) {
      const sent = await run(turnPath(kept), body, text, true);
      if (sent !== 'gone') return sent;
      // P2 review F5: the remembered conversation is gone. Forget it, start a new one and send there, once.
      current.current = null;
      setConversationId(null);
    }
    const id = await start();
    if (id === null) return 'refused' as const;
    return run(turnPath(id), body, text, false) as Promise<'sent' | 'refused'>;
  }, [run, page, feel, setConversationId]);

  const act = useCallback(async (actionId: Id, kind: 'confirm' | 'reject', callId?: string | null, tap?: ActTap) => {
    if (busy.current) return 'refused' as const;
    const call = callId === undefined ? calls.current.get(actionId) ?? null : callId;
    if (kind === 'confirm' && call !== null) feel({ kind: 'confirmed', callId: call });
    feel({ kind: 'pending', open: false });
    return run(actionPath(actionId, kind), {}, null, false, tap) as Promise<'sent' | 'refused'>;
  }, [feel, run]);
  const callOf = useCallback((actionId: Id) => calls.current.get(actionId) ?? null, []);

  const clear = useCallback(() => {
    abort.current?.abort();
    writer.current?.stop();
    setLive(null);
    setRefusal(null);
    setPoll(null);
  }, []);

  return { live, running, send, stream, act, callOf, refusal, clear, pollUntil: poll?.until ?? null };
}
